import {Directive, DOCUMENT, effect, Inject, OnInit} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {Util} from "../util/util";
import {SessionService} from "../common/session.service";
import {LocaleUtil} from "../util/locale-util";


@Directive({
    selector: 'seo-head',
    standalone: false
})
export class SeoHeadDirective implements OnInit {

    link: string = "";
    currentUrl: string = "";

    constructor(
        private router: Router,
        private sessionService: SessionService,
        @Inject(DOCUMENT) private dom: Document
    ) {
        effect(() => {
            const countryLocales = this.sessionService.countryLocales();
            const usanaLocale = this.sessionService.usanaLocaleSubject.value;

            this.setHtmlLang(usanaLocale);
            this.setMetaContentLang(usanaLocale);
            this.updateHrefLangTags(countryLocales);
        });
    }

    ngOnInit() {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.currentUrl = event.url;
            this.setCanonicalLink(Util.getCanonicalUrl());
            this.updateHrefLangTags(this.sessionService.countryLocales());
        });
    }

    private convertLanguageToBcp47(lang: string): string {
        const langMap: { [key: string]: string } = {
            'kr': 'ko',  // Korean
            'jp': 'ja'   // Japanese
        };
        return langMap[lang] || lang;
    }

    private convertLocaleToBcp47(locale: string): string {
        const [lang, country] = locale.split('-');
        return `${this.convertLanguageToBcp47(lang)}-${country}`;
    }

    updateHrefLangTags(countryLocales: string[]) {
        if (this.currentUrl && countryLocales.length > 0) {
            countryLocales.forEach(usanaLocale => {
                this.setHrefLang(this.currentUrl, usanaLocale);
            });
        }
        this.setDefaultHrefLang(this.currentUrl);
    }

    setHrefLang(url: string, usanaLocale: string) {
        const head = this.dom.getElementsByTagName('head')[0];
        let element: HTMLLinkElement= this.dom.querySelector(`link[rel='alternate'][hreflang='${this.convertLocaleToBcp47(usanaLocale)}']`) || null;
        if (element == null) {
            element = this.dom.createElement('link') as HTMLLinkElement;
            head.appendChild(element);
        }
        element.setAttribute('rel', 'alternate');
        element.setAttribute('hreflang', this.convertLocaleToBcp47(usanaLocale));
        const urlArr: string[] = url.split('/');
        LocaleUtil.replaceLocale(urlArr, usanaLocale);
        // url already includes the section prefix (e.g. /ux/cart/... or /ux/dotcom/...),
        // so don't prepend URL_BASE_CART here.
        element.setAttribute('href', `${Util.getBaseUrl()}${urlArr.join('/')}`);
    }

    setDefaultHrefLang(url: string) {
        const head = this.dom.getElementsByTagName('head')[0];
        let element: HTMLLinkElement= this.dom.querySelector(`link[rel='alternate'][hreflang='x-default']`) || null;
        if (element == null) {
            element = this.dom.createElement('link') as HTMLLinkElement;
            head.appendChild(element);
        }
        element.setAttribute('rel', 'alternate');
        element.setAttribute('hreflang', 'x-default');
        element.setAttribute('href', `${Util.getBaseUrl()}${url}`);
    }

    setHtmlLang(usanaLocale: string) {
        const head = this.dom.getElementsByTagName('head')[0];
        let element: HTMLHtmlElement= this.dom.querySelector('html') || null;
        if (element == null) {
            element = this.dom.createElement('html') as HTMLHtmlElement;
            head.appendChild(element);
        }
        element.setAttribute('lang', this.convertLanguageToBcp47(usanaLocale.split('-')[0]));
    }

    setMetaContentLang(usanaLocale: string) {
        const head = this.dom.getElementsByTagName('head')[0];
        let element: HTMLMetaElement = this.dom.querySelector(`meta[http-equiv='Content-Language']`) || null;
        if (element == null) {
            element = this.dom.createElement('meta') as HTMLMetaElement;
            head.appendChild(element);
        }
        element.setAttribute('http-equiv', 'Content-Language');
        element.setAttribute('content', this.convertLocaleToBcp47(usanaLocale));
    }

    setCanonicalLink(link: string) {
        const head = this.dom.getElementsByTagName('head')[0];
        let element: HTMLLinkElement= this.dom.querySelector(`link[rel='canonical']`) || null;
        if (element == null) {
            element = this.dom.createElement('link') as HTMLLinkElement;
            head.appendChild(element);
        }
        element.setAttribute('rel', 'canonical');
        element.setAttribute('href', link);
    }
}
